import { useState } from "react";
import { Archive, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton, ChalkCard } from "./primitives";

/**
 * Wrap this season — 2-step BottomSheet:
 *   1. Confirm intent + optional new-season label
 *   2. Done card with summary (cancelled events count + new team link)
 */
export default function WrapSeasonSheet({ isOpen, team, onDismiss, onWrapped }) {
  const [startNew, setStartNew] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(null);

  async function wrap() {
    if (!team) return;
    setRunning(true);
    try {
      const data = await api.archiveSeason(team.id, {
        start_new: startNew,
        new_season_label: newLabel.trim(),
      });
      setDone(data);
      toast.success("Season wrapped.");
    } catch (e) {
      toast.error(e.message || "Couldn't wrap the season.");
    } finally {
      setRunning(false);
    }
  }

  function close() {
    setDone(null);
    setNewLabel("");
    setStartNew(true);
    onDismiss && onDismiss();
    if (done) onWrapped && onWrapped(done);
  }

  return (
    <BottomSheet isOpen={isOpen} title="Wrap this season" onDismiss={close}>
      {!done ? (
        <div data-testid="wrap-season-confirm" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 14, color: "var(--chalk-board)", lineHeight: 1.45 }}>
            Archiving <b>{team?.season_label || "this season"}</b> for <b>{team?.name}</b>. Future events will be cancelled and the board goes read-only. The roster carries forward.
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: "var(--chalk-board)", cursor: "pointer" }}>
            <input
              type="checkbox"
              data-testid="wrap-start-new"
              checked={startNew}
              onChange={(e) => setStartNew(e.target.checked)}
            />
            Start a fresh season with the same roster
          </label>

          {startNew && (
            <div>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6 }}>
                New season label
              </div>
              <input
                data-testid="wrap-new-label"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="Fall 2026"
                style={{
                  width: "100%", border: "0.5px solid var(--chalk-border)",
                  borderRadius: 10, padding: "10px 12px", fontSize: 14, outline: "none",
                  background: "#FBF8F2", color: "var(--chalk-board)",
                }}
              />
            </div>
          )}

          <ChalkActionButton
            testId="wrap-confirm"
            variant="danger"
            label={running ? "Wrapping…" : "Wrap this season"}
            icon={<Archive size={14} aria-hidden="true" />}
            loading={running}
            onClick={wrap}
          />
          <ChalkActionButton
            testId="wrap-cancel"
            variant="ghost"
            label="Not yet"
            onClick={close}
          />
        </div>
      ) : (
        <div data-testid="wrap-season-done" style={{ display: "grid", gap: 12 }}>
          <ChalkCard style={{ background: "#FBF8F2" }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-green)", marginBottom: 6 }}>Archived</div>
            <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>
              {done.season_label}
            </div>
            <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
              The board is now read-only.
            </div>
          </ChalkCard>
          {done.new_team && (
            <ChalkCard>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--chalk-muted)", marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={12} aria-hidden="true" /> Next up
              </div>
              <div style={{ fontSize: 15, color: "var(--chalk-board)", fontWeight: 500 }}>
                {done.new_team.name} · {done.new_team.season_label}
              </div>
              <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
                {done.new_team.athlete_count} athlete{done.new_team.athlete_count === 1 ? "" : "s"} carried forward.
              </div>
            </ChalkCard>
          )}
          <ChalkActionButton
            testId="wrap-done"
            variant="primary"
            label="Take me to the new board"
            onClick={close}
          />
        </div>
      )}
    </BottomSheet>
  );
}
